// Usage and billing.  /analytics/usage
//
// Billable interactions against the platform quota, broken down by flow and,
// where more than one organization can appear, by organization.
//
// The quota meter has a fixed denominator from the server, so the percentage
// is always measured. The per-row percentages come from the server the same
// way. Nothing on this screen is a rate over an empty denominator.

import { useMemo, useState } from "react";

import { http, qs } from "../api";
import { useSession } from "../App";
import { Badge, Banner, Empty, SkeletonRows, Table, useAsync, type Column } from "../ui";
import { STATUS } from "../theme";
import { ExportMenu, localDate, nf, type DateRange } from "./shared";

type UsagePeriod = "this_month" | "last_month" | "last_3_months";

interface UsageSummary {
  period: { from: string; to: string };
  total_interactions: number;
  included: number;
  overage_count: number;
}

interface UsageFlowRow {
  flow_id?: string;
  flow_name: string;
  count: number;
  percent: number;
}

interface UsageOrgRow {
  organization_id: string;
  organization_name: string;
  count: number;
  percent: number;
}

interface MyOrganization {
  organization_id: string;
  name: string;
  slug: string;
  role_name?: string | null;
  is_default: boolean;
}

const USAGE_PERIOD_LABEL: Record<UsagePeriod, string> = {
  this_month: "This month",
  last_month: "Last month",
  last_3_months: "Last 3 months",
};

/**
 * Billing months are whole months, not "up to today" — a quota is only
 * meaningful against the full period it is charged for. That is why this does
 * not reuse the shared preset helper.
 */
function usageRange(period: UsagePeriod): DateRange {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  if (period === "last_month") {
    return { from: localDate(new Date(y, m - 1, 1)), to: localDate(new Date(y, m, 0)) };
  }
  if (period === "last_3_months") {
    return { from: localDate(new Date(y, m - 2, 1)), to: localDate(new Date(y, m + 1, 0)) };
  }
  return { from: localDate(new Date(y, m, 1)), to: localDate(new Date(y, m + 1, 0)) };
}

function periodLabel(summary: UsageSummary | null): string {
  if (!summary) return "";
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" };
  const from = new Date(summary.period.from);
  const to = new Date(summary.period.to);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return "";
  return `${from.toLocaleDateString(undefined, opts)} – ${to.toLocaleDateString(undefined, opts)}`;
}

export function UsageView() {
  const { me } = useSession();
  const [period, setPeriod] = useState<UsagePeriod>("this_month");
  const range = useMemo(() => usageRange(period), [period]);

  const orgsQuery = useAsync(
    () => http.get<{ organizations: MyOrganization[] }>("/me/organizations"),
    []
  );

  const query = useAsync(
    () =>
      Promise.all([
        http.get<UsageSummary>(`/billing/usage${qs({ from: range.from, to: range.to })}`),
        http.get<UsageFlowRow[]>(`/billing/usage/by-flow${qs({ from: range.from, to: range.to })}`),
        http.get<UsageOrgRow[]>(`/billing/usage/by-org${qs({ from: range.from, to: range.to })}`),
      ]).then(([summary, byFlow, byOrg]) => ({
        summary,
        byFlow: byFlow ?? [],
        byOrg: byOrg ?? [],
      })),
    [range.from, range.to]
  );

  const orgs = orgsQuery.data?.organizations ?? [];
  const isMultiOrg = Boolean(me?.is_super_admin) || orgs.length > 1;
  const orgName =
    orgs.length === 1
      ? orgs[0].name
      : orgs.find((o) => o.organization_id === me?.organization_id)?.name ?? "";

  const title = isMultiOrg
    ? "Usage: overall"
    : orgName
      ? `Usage: ${orgName}`
      : "Usage";

  const summary = query.data?.summary ?? null;
  const usedPercent =
    summary && summary.included > 0
      ? Math.min(100, (summary.total_interactions / summary.included) * 100)
      : 0;
  const overQuota = Boolean(summary && summary.overage_count > 0);
  const meterColor = overQuota ? STATUS.critical : usedPercent >= 80 ? STATUS.warning : STATUS.good;

  const flowColumns: Column<UsageFlowRow>[] = [
    { key: "flow_name", header: "Flow name", sortable: true },
    {
      key: "count",
      header: "Count",
      align: "right",
      sortable: true,
      render: (r) => <span className="num">{nf.format(r.count)}</span>,
    },
    {
      key: "percent",
      header: "%",
      align: "right",
      sortable: true,
      render: (r) => <span className="num">{r.percent.toFixed(1)}%</span>,
    },
  ];

  const orgColumns: Column<UsageOrgRow>[] = [
    { key: "organization_name", header: "Organization", sortable: true },
    {
      key: "count",
      header: "Count",
      align: "right",
      sortable: true,
      render: (r) => <span className="num">{nf.format(r.count)}</span>,
    },
    {
      key: "percent",
      header: "%",
      align: "right",
      sortable: true,
      render: (r) => <span className="num">{r.percent.toFixed(1)}%</span>,
    },
  ];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>{title}</h1>
          <div className="sub">Billable interactions against the platform quota.</div>
        </div>
        <div className="page-actions">
          <select
            value={period}
            aria-label="Billing period"
            style={{ width: 200 }}
            onChange={(e) => setPeriod(e.target.value as UsagePeriod)}
          >
            {(Object.keys(USAGE_PERIOD_LABEL) as UsagePeriod[]).map((p) => (
              <option key={p} value={p}>
                {USAGE_PERIOD_LABEL[p]}
              </option>
            ))}
          </select>
          {summary && <span className="muted nowrap">{periodLabel(summary)}</span>}
          <ExportMenu snapshot="billing" range={range} rawDataset="billing_interactions" />
        </div>
      </div>

      {query.error && (
        <Banner tone="critical" traceId={query.error.traceId}>
          {/* Billing reads are gated on multi-org access, so a single-org
              operator lands here with a 403 rather than an empty page. */}
          Could not load usage: {query.error.message}
        </Banner>
      )}

      <div className="card" style={{ marginBottom: "var(--sp-4)" }}>
        <h3>Interaction quota</h3>
        <div className="muted">Billable conversations counted against the included allowance.</div>
        {query.loading ? (
          <SkeletonRows n={3} cols={2} />
        ) : !summary ? (
          <Empty title="No usage to show" body="Pick another period, or check the error above." icon="∑" />
        ) : (
          <div style={{ marginTop: "var(--sp-3)" }}>
            <div className="row">
              <span className="strong num" style={{ fontSize: 26 }}>
                {nf.format(summary.total_interactions)}
              </span>
              <span className="muted">/ {nf.format(summary.included)} interactions</span>
            </div>
            <div className="meter" style={{ marginTop: "var(--sp-3)" }}>
              <span style={{ width: `${usedPercent}%`, background: meterColor }} />
            </div>
            <div style={{ marginTop: "var(--sp-3)" }}>
              {overQuota ? (
                <Badge tone="critical">
                  {nf.format(summary.overage_count)} overage interactions
                </Badge>
              ) : (
                <span className="muted">Within quota.</span>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="grid c2">
        <div className="card flush">
          <div className="card-head">
            <h3>By flow</h3>
          </div>
          <Table
            columns={flowColumns}
            rows={query.data?.byFlow ?? []}
            loading={query.loading}
            empty="No interactions in this period"
          />
        </div>

        {/* A single-org breakdown is a trivial 100%, so it is only drawn when
            more than one organization can appear in it. */}
        {isMultiOrg && (
          <div className="card flush">
            <div className="card-head">
              <h3>By organization</h3>
            </div>
            <Table
              columns={orgColumns}
              rows={query.data?.byOrg ?? []}
              loading={query.loading}
              empty="No interactions in this period"
            />
          </div>
        )}
      </div>
    </div>
  );
}
